import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { useEffect, useMemo, useState } from "react";
import { Check, Search } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@bookdental/ui";
import { cn } from "@/lib/cn";

export type OpcaoDeFiltro = {
  valor: string;
  rotulo: string;
  cor?: string;
  fundo?: string;
};

type Props = {
  aberto: boolean;
  titulo: string;
  opcoes: OpcaoDeFiltro[];
  /** Valores marcados hoje. A folha trabalha numa cópia até o usuário aplicar. */
  selecionados: string[];
  /** Mostra busca interna — usado no filtro de estágio, que pode ter muitas opções. */
  comBusca?: boolean;
  /** Escolha única (dentista, clínica): marcar uma desmarca a outra. */
  unico?: boolean;
  onFechar: () => void;
  onAplicar: (valores: string[]) => void;
};

/**
 * Folha inferior de um filtro no celular.
 *
 * Reaproveita o `Sheet` do design system, que já traz foco preso, fechamento por Escape e clique
 * fora, e devolve o foco ao gatilho — nada disso precisou ser reimplementado.
 *
 * A seleção é editada numa cópia local e só volta pro estado da página em "Aplicar": no celular a
 * folha cobre a lista, e aplicar a cada toque faria o resultado mudar atrás de algo que o usuário
 * não está vendo.
 */
export function MobileFilterSheet({
  aberto,
  titulo,
  opcoes,
  selecionados,
  comBusca = false,
  unico = false,
  onFechar,
  onAplicar,
}: Props) {
  const [rascunho, setRascunho] = useState<string[]>(selecionados);
  const [busca, setBusca] = useState("");

  // Reabrir sempre parte do que está valendo agora, não do rascunho abandonado antes.
  useEffect(() => {
    if (aberto) {
      setRascunho(selecionados);
      setBusca("");
    }
  }, [aberto, selecionados]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return opcoes;
    return opcoes.filter((o) => o.rotulo.toLowerCase().includes(termo));
  }, [opcoes, busca]);

  const alternar = (valor: string) =>
    setRascunho((atuais) =>
      atuais.includes(valor) ? atuais.filter((v) => v !== valor) : unico ? [valor] : [...atuais, valor],
    );

  return (
    <Sheet open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <SheetContent
        side="bottom"
        className="flex max-h-[85dvh] flex-col gap-0 rounded-t-2xl border-zinc-200 bg-white p-0 dark:border-border dark:bg-card"
      >
        <SheetHeader className="border-b border-zinc-100 px-4 py-3 text-left dark:border-border">
          <SheetTitle className="text-sm font-bold text-zinc-900 dark:text-foreground" ajuda={AJUDA_PRODUCAO.filtroCelular}>
            {titulo}
            {rascunho.length > 0 && (
              <span className="ml-1.5 text-xs font-semibold text-brand-700 dark:text-primary">
                {rascunho.length}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {comBusca && (
          <div className="relative border-b border-zinc-100 px-4 py-2 dark:border-border">
            <Search className="pointer-events-none absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={`Buscar em ${titulo.toLowerCase()}...`}
              className="h-11 w-full rounded-md border border-zinc-200 bg-white pl-8 pr-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-background dark:text-foreground"
            />
          </div>
        )}

        <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
          {visiveis.length === 0 ? (
            <li className="px-3 py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">
              Nenhuma opção encontrada.
            </li>
          ) : (
            visiveis.map((o) => {
              const marcado = rascunho.includes(o.valor);
              return (
                <li key={o.valor}>
                  <button
                    type="button"
                    onClick={() => alternar(o.valor)}
                    aria-pressed={marcado}
                    className="flex h-12 w-full items-center gap-3 rounded-md px-3 text-left text-sm text-zinc-800 transition-colors hover:bg-zinc-50 dark:text-foreground dark:hover:bg-muted"
                  >
                    <span
                      className={cn(
                        "flex h-5 w-5 shrink-0 items-center justify-center border",
                        unico ? "rounded-full" : "rounded",
                        marcado
                          ? "border-brand-600 bg-brand-600 text-white dark:border-primary dark:bg-primary"
                          : "border-zinc-300 dark:border-border",
                      )}
                    >
                      {marcado && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                    </span>
                    <span
                      className="min-w-0 flex-1 truncate"
                      style={o.cor ? { color: o.cor } : undefined}
                    >
                      {o.rotulo}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>

        <div className="flex gap-2 border-t border-zinc-100 p-3 dark:border-border">
          <button
            type="button"
            onClick={() => setRascunho([])}
            className="h-11 flex-1 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Limpar
          </button>
          <button
            type="button"
            onClick={() => {
              onAplicar(rascunho);
              onFechar();
            }}
            className="h-11 flex-[1.4] rounded-md bg-brand-600 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
          >
            Aplicar
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
